/**
 * @fileoverview useEntityMatch — Fuzzy-matches an imported name against a list of entities.
 *
 * Returns the best match (if above threshold) and the imported name.
 * The step uses this to auto-select and mark as assisted.
 * ImportSuggestion uses this to show the "no match" fallback text.
 */
import { useMemo } from 'react';

export interface MatchOption {
  id: string;
  label: string;
}

export interface EntityMatchResult {
  /** The raw name from the import */
  importedName: string | null;
  /** Best fuzzy match, or null if none above threshold */
  bestMatch: MatchOption | null;
  /** Whether a strong match was found */
  hasMatch: boolean;
}

function fuzzyScore(query: string, target: string): number {
  const normalizedQuery = query.toLowerCase().trim();
  const normalizedTarget = target.toLowerCase().trim();

  if (!normalizedQuery || !normalizedTarget) {
    return 0;
  }

  if (normalizedQuery === normalizedTarget) {
    return 1;
  }

  if (normalizedTarget.includes(normalizedQuery)) {
    return 0.9;
  }

  if (normalizedQuery.includes(normalizedTarget)) {
    return 0.85;
  }

  const queryTokens = normalizedQuery.split(/\s+/);
  const targetTokens = normalizedTarget.split(/\s+/);
  const matchedTokenCount = queryTokens.filter((queryToken) =>
    targetTokens.some(
      (targetToken) =>
        targetToken.includes(queryToken) || queryToken.includes(targetToken),
    ),
  ).length;
  const tokenScore = queryTokens.length > 0 ? matchedTokenCount / queryTokens.length : 0;

  const matchedCharacters = normalizedTarget.split('').reduce(
    (queryIndex, character) =>
      normalizedQuery[queryIndex] === character ? queryIndex + 1 : queryIndex,
    0,
  );
  const characterScore = matchedCharacters / normalizedQuery.length;

  return Math.max(tokenScore * 0.9, characterScore * 0.7);
}

/**
 * Fuzzy-match an imported name against entity options.
 */
export function useEntityMatch(
  importedName: string | null | undefined,
  options: MatchOption[],
  threshold = 0.7,
): EntityMatchResult {
  const bestMatch = useMemo(() => {
    if (!importedName?.trim() || options.length === 0) {
      return null;
    }

    const bestScoredMatch = options.reduce<(MatchOption & { score: number }) | null>(
      (currentBest, option) => {
        const score = fuzzyScore(importedName, option.label);

        if (score <= (currentBest?.score ?? 0)) {
          return currentBest;
        }

        return { ...option, score };
      },
      null,
    );

    if (!bestScoredMatch || bestScoredMatch.score < threshold) {
      return null;
    }

    return {
      id: bestScoredMatch.id,
      label: bestScoredMatch.label,
    };
  }, [importedName, options, threshold]);

  return {
    importedName: importedName?.trim() || null,
    bestMatch,
    hasMatch: Boolean(bestMatch),
  };
}
